import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BackButton, Box, Column, Row, Text } from '~/commonComponents';
import { clearAllReceipts, fetchJobsWithTransactions, fetchReceiptList } from '../../../actions';
import { paymentHistoryFilterOptions } from '../../../constants';
import '../../../themes/global.scss';
import { RequestReportPopup, Toolbar } from './components';
import { ReceiptTableV2 } from './components/AllReceiptTable/ReceiptTableV2';
import ShiftReceiptTable from './ShiftReceiptTable';
import { getFilterDateRage } from './utils';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { EnforcePaymentHistoryTable } from '~/constants/ff.posthog';

const LIMIT_VALUE = 25;

export default function ReceiptList() {
  const dispatch = useDispatch();
  const receiptsScrollRef = useRef(null);

  const isFinalPage = useSelector((state) => state.job.isFinalPage);
  const isActiveRequest = useSelector((state) => state.job.isActiveRequest);

  const jobsWithTransactionPagination = useSelector(
    (state) => state.job.jobsWithTransactionPagination,
  );
  const receiptsPagination = useSelector((state) => state.job.receiptsPagination);
  const receiptDisplayMode = useSelector((state) => state.job.receiptDisplayMode);
  const [sort, setSort] = useState({
    field: 'date',
    order: 'desc',
  });
  const [filter, setFilter] = useState(paymentHistoryFilterOptions.ALL);
  const [isShowRequestReportPopup, setIsShowRequestReportPopup] = useState(false);

  const enforcePaymentHistoryTable = useFeatureFlag(EnforcePaymentHistoryTable);

  useEffect(() => {
    // Reset filter when changing display mode
    setFilter(paymentHistoryFilterOptions.ALL);
  }, [receiptDisplayMode]);

  useEffect(() => {
    dispatch(clearAllReceipts());

    if (receiptDisplayMode === 'shift') {
      dispatch(
        fetchJobsWithTransactions({
          page: 1,
          limit: LIMIT_VALUE,
          sort: sort.field,
          order: sort.order,
        }),
      );
    }
  }, [dispatch, receiptDisplayMode, sort, filter]);

  const handleScroll = () => {
    const receiptsScroll = receiptsScrollRef.current;

    const userScrollHeight = receiptsScroll.scrollHeight - receiptsScroll?.scrollTop - 20;
    const clientHeight = receiptsScroll?.clientHeight;

    if (receiptDisplayMode === 'all') {
      if (userScrollHeight <= clientHeight && !isActiveRequest && !isFinalPage) {
        dispatch(
          fetchReceiptList({
            page: receiptsPagination?.page + 1,
            limit: LIMIT_VALUE,
            sort: sort.field,
            order: sort.order,
            ...getFilterDateRage(filter),
          }),
        );
      }
    } else if (
      userScrollHeight <= clientHeight &&
      !isActiveRequest &&
      !jobsWithTransactionPagination?.isFinal
    ) {
      dispatch(
        fetchJobsWithTransactions({
          page: jobsWithTransactionPagination?.page + 1,
          limit: LIMIT_VALUE,
        }),
      );
    }
  };

  const changeSort = (sortName) => {
    setSort({
      field: sortName,
      order: sort.order === 'asc' ? 'desc' : 'asc',
    });

    if (receiptsScrollRef.current) {
      receiptsScrollRef.current.scrollTop = 0;
    }
  };

  return (
    <>
      <div className="component_container" data-cy="payment-history-container">
        <div
          className="floating_container"
          style={{
            position: 'relative',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: 40,
            maxHeight: '90vh',
          }}
        >
          {enforcePaymentHistoryTable ? (
            <Row position="relative" alignItems="center" justifyContent="center" width="100%">
              <Box position="absolute" left={0}>
                <BackButton />
              </Box>
              <Column alignItems="center" justifyContent="center">
                <Text bold fontSize={32}>
                  Payment History
                </Text>
                <Text fontSize={22}>
                  {receiptDisplayMode === 'shift' ? `By Posting` : 'All Transactions'}
                </Text>
              </Column>
            </Row>
          ) : (
            <>
              <div style={{ position: 'absolute', left: 50 }}>
                <BackButton />
              </div>

              <h1
                className="global_font f-dark"
                style={{ textAlign: 'center', marginBottom: 10 }}
                data-cy="payment-history-title"
              >
                Payment History
              </h1>

              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <motion.h3
                  key={receiptDisplayMode}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="global_font f-dark"
                  style={{ fontSize: 22, marginTop: 0 }}
                >
                  {receiptDisplayMode === 'shift' ? `By Shift` : 'All Transactions'}
                </motion.h3>
              </div>
            </>
          )}

          <Toolbar
            displayMode={receiptDisplayMode}
            filter={filter}
            onFilterChange={(val) => setFilter(val)}
            onShowRequestReportPopup={() => setIsShowRequestReportPopup(true)}
          />

          {receiptDisplayMode === 'all' ? (
            <ReceiptTableV2
              filters={{
                transactionDate: filter,
              }}
            />
          ) : (
            <ShiftReceiptTable
              ref={receiptsScrollRef}
              onScroll={handleScroll}
              sort={sort}
              onSort={changeSort}
            />
          )}
        </div>
      </div>
      {isShowRequestReportPopup && (
        <RequestReportPopup onClose={() => setIsShowRequestReportPopup(false)} />
      )}
    </>
  );
}
